import { computed, effect, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Params, Router } from '@angular/router';
import { map, Observable, Subscription } from 'rxjs';
import { RunAgentInput } from '@app/core/agent-api/models/run-agent-input';
import { AguiContextItem } from '@app/core/agent-api/models/agui-context-item';
import { StingrayAuthenticationService } from '@app/core/authentication/stingray-authentication.service';
import { Api as AgentApi } from '@app/core/agent-api/api';
import { cancelResponse } from '@app/core/agent-api/fn/responses-http-handler/cancel-response';
import { environment } from '../../../environments/environment';
import { McpAuthService } from './mcp-auth.service';
import { DataChangeService } from './data-change.service';

export interface AppLink {
  label: string;
  url: string;
  routerCommands: any[];
  queryParams?: Params;
}

/** Where an app:// link navigates to: the router commands and, for a page that selects by query parameters, those. */
export interface AppLinkTarget {
  commands: any[];
  queryParams?: Params;
}


function parseAppLink(url: string): AppLinkTarget | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'app:') return null;
    const resource = parsed.hostname;
    const id = parsed.pathname.replace(/^\//, '');
    const customerId = parsed.searchParams.get('customerId');
    const detail = (commands: any[]): AppLinkTarget => ({ commands });

    switch (resource) {
      case 'building':
        return customerId ? detail(['/customers', customerId, 'buildings', id, 'detail']) : null;
      case 'customer':
        return detail(['/customers', id, 'detail']);
      case 'application':
        return customerId ? detail(['/customers', customerId, 'applications', id, 'detail']) : null;
      case 'financing':
        return customerId ? detail(['/customers', customerId, 'financings', id, 'detail']) : null;
      case 'mortgage':
        return customerId ? detail(['/customers', customerId, 'mortgages', id, 'detail']) : null;
      case 'valuation':
        return customerId ? detail(['/customers', customerId, 'valuations', id, 'detail']) : null;
      case 'task':
        return detail(['/tasks', id, 'detail']);
      case 'rate': {
        // A lender rate (lenderNumber_rateNumber) opens in the rate details tab of the rate settings
        const [lender, rate] = id.split('_');
        return lender && rate ? { commands: ['/settings', 'rates'], queryParams: { tab: 'rate-details', lender, rate } } : null;
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
}

export interface PendingAttachment {
  fileId: string;
  fileName: string;
}

export interface McpApprovalRequest {
  id: string;
  toolName: string;
  serverLabel: string;
  arguments: string;
  previousResponseId: string;
}

export interface OAuthConsentRequest {
  id: string;
  provider: string;
  scopes?: string[];
  authorizationUrl: string;
  previousResponseId: string;
}

export interface QuestionOption {
  label: string;
  value: string;
}

export interface PendingQuestion {
  question: string;
  options: QuestionOption[];
  allowOther?: boolean;
  otherLabel?: string;
}

type AgentRunBody = {
  threadId?: string;
  messages?: Array<any>;
  mcpApprovalRequestId?: string;
  mcpApprovalApproved?: boolean;
  oauthConsentRequestId?: string;
  oauthConsentApproved?: boolean;
};

/** A stored conversation of the signed-in user, from the agent API's /chat-history endpoints. */
export interface ChatConversationSummary {
  id: string;
  title?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A document-processing run that needs the signed-in user's attention, from the agent API's /workflow-runs/pending
 * endpoint — e.g. a rental list uploaded in Unite that the assistant has prepared, or an upload it stopped on.
 */
export interface PendingWorkflowRun {
  runId: string;
  conversationId: string;
  title?: string | null;
  /** 'upload' when the run was started by a document uploaded in Unite, 'chat' otherwise. */
  source: 'upload' | 'chat';
  /**
   * 'waiting' when the run waits for an answer; 'running' for an upload the assistant is still working on (its
   * conversation can be followed); 'finished' for an upload it handled without a question (e.g. it could not find the
   * buildings), listed until someone opens the conversation.
   */
  state: 'waiting' | 'running' | 'finished';
  pendingPort?: string | null;
  /** The open question as a question_request payload ({ question, options, allowOther, otherLabel }). */
  question?: any;
  createdAt: string;
  updatedAt: string;
}

interface ChatConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  attachmentNames?: string[];
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming: boolean;
  attachmentNames?: string[];
  appLinks?: AppLink[];
}

@Injectable({ providedIn: 'root' })
export class ChatService {
  private agentUrl: string;
  // The thread on screen, as a signal so otherPendingRuns can leave it out of the "waiting for you" list.
  private readonly activeConversationId = signal<string | null>(null);
  private get conversationId(): string | null { return this.activeConversationId(); }
  private set conversationId(value: string | null) { this.activeConversationId.set(value); }
  private streamAbortController: AbortController | null = null;
  private currentResponseId: string | null = null;
  private oauthSubscription: Subscription | null = null;
  private lastUserMessage = '';

  readonly messages = signal<ChatMessage[]>([]);
  readonly isStreaming = signal(false);
  readonly hasError = signal(false);
  readonly pendingAttachments = signal<PendingAttachment[]>([]);
  readonly isUploading = signal(false);
  readonly pendingApproval = signal<McpApprovalRequest | null>(null);
  readonly pendingOAuthConsent = signal<OAuthConsentRequest | null>(null);
  readonly pendingQuestion = signal<PendingQuestion | null>(null);
  readonly oauthPopupInProgress = signal(false);
  readonly conversations = signal<ChatConversationSummary[]>([]);
  readonly isLoadingConversations = signal(false);
  readonly isLoadingConversation = signal(false);
  readonly pendingRuns = signal<PendingWorkflowRun[]>([]);
  /** Pending runs other than the conversation on screen: the ones worth pointing the user to while the chat is open. */
  readonly otherPendingRuns = computed(() =>
    this.pendingRuns().filter(run => run.conversationId !== this.activeConversationId()));
  /**
   * Whether the conversation on screen is an upload the assistant is still working on: it fills in as the assistant
   * goes (refreshed on every push of the agent's hub) and cannot be answered until the run halts or ends.
   */
  readonly activeRunWorking = computed(() => {
    const active = this.activeConversationId();
    return !!active && this.pendingRuns().some(run => run.conversationId === active && run.state === 'running');
  });
  /** Whether the chat window shows the recent-conversations panel; shared so opening a run can close it. */
  readonly showHistory = signal(false);

  constructor(
    private authService: StingrayAuthenticationService,
    private http: HttpClient,
    private agentApi: AgentApi,
    private mcpAuthService: McpAuthService,
    private dataChangeService: DataChangeService,
    private router: Router,
  ) {
    this.agentUrl = `${environment.agentApiBaseUrl}/agui`;

    // A finished turn may have answered (or created) a pending run, so refresh the list.
    let wasStreaming = false;
    effect(() => {
      const streaming = this.isStreaming();
      if (wasStreaming && !streaming) this.loadPendingRuns();
      wasStreaming = streaming;
    });
  }

  sendMessage(userText: string): void {
    if (this.isStreaming() || !userText.trim()) return;

    const attachments = this.pendingAttachments();
    this.pendingAttachments.set([]);

    const text = userText.trim();

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      isStreaming: false,
      attachmentNames: attachments.map(a => a.fileName),
    };

    // Backend convention: file references are embedded in the message content
    // as [[file:<id>]] markers rather than sent as a separate field.
    const markers = attachments.map(a => `[[file:${a.fileId}]]`).join(' ');
    const content = markers ? `${text}\n\n${markers}` : text;

    this.lastUserMessage = content;
    this.messages.update(msgs => [...msgs, userMessage]);
    this.isStreaming.set(true);
    this.hasError.set(false);
    this.pendingApproval.set(null);
    this.pendingOAuthConsent.set(null);
    this.pendingQuestion.set(null);

    const body: AgentRunBody = {
      messages: [{
        role: 'user',
        content,
      }],
    };

    this.authService.getTokenSilently$().subscribe({
      next: (tokenResponse) => {
        const token = typeof tokenResponse === 'string' ? tokenResponse : (tokenResponse as any).access_token as string;

        if (!this.conversationId) {
          this.http.post<{ id?: string; conversationId?: string }>(`${environment.agentApiBaseUrl}/v1/conversations`, {}).subscribe({
            next: (result) => {
              this.conversationId = result.id ?? result.conversationId ?? '';
              body.threadId = this.conversationId;
              this.startStream(body, token);
            },
            error: () => {
              this.isStreaming.set(false);
              this.hasError.set(true);
            },
          });
        } else {
          body.threadId = this.conversationId;
          this.startStream(body, token);
        }
      },
      error: () => {
        this.isStreaming.set(false);
        this.hasError.set(true);
      },
    });
  }

  /**
   * Answers a pending workflow question (e.g. "process this document?") by sending the chosen
   * option's value as a normal user message. The backend resumes on the keyword in that value,
   * so no dedicated endpoint is needed — this is just a scripted send.
   */
  answerQuestion(value: string): void {
    const question = this.pendingQuestion();
    if (!question) return;
    this.pendingQuestion.set(null);
    // Keep the question visible in the transcript once answered, in order before the reply.
    this.recordAnsweredQuestion(question);
    this.sendMessage(value);
  }

  /**
   * Dismisses a pending question without sending anything, so the user can type their own reply.
   * The backend keeps the stashed intent until the next user turn, so whatever they type next
   * resumes that flow (a free question is answered by the assistant with the document in context).
   */
  dismissQuestion(): void {
    const question = this.pendingQuestion();
    if (!question) return;
    this.pendingQuestion.set(null);
    // Keep the question visible in the transcript so the user still sees what they're replying to.
    this.recordAnsweredQuestion(question);
  }

  /** Appends an answered/dismissed question to the transcript as a normal assistant bubble. */
  private recordAnsweredQuestion(question: PendingQuestion): void {
    this.messages.update(msgs => [...msgs, {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: question.question,
      isStreaming: false,
    }]);
  }

  submitApproval(approved: boolean): void {
    const approval = this.pendingApproval();
    if (!approval) return;

    this.pendingApproval.set(null);

    if (!approved) return;

    this.isStreaming.set(true);
    this.hasError.set(false);

    this.authService.getTokenSilently$().subscribe({
      next: (tokenResponse) => {
        const token = typeof tokenResponse === 'string' ? tokenResponse : (tokenResponse as any).access_token as string;
        this.http.post(
          `${environment.agentApiBaseUrl}/mcp-approval`,
          { threadId: this.conversationId, tool: approval.toolName },
          { headers: { 'Authorization': `Bearer ${token}` } },
        ).subscribe({
          next: () => this.resendLastMessage(),
          error: () => {
            this.isStreaming.set(false);
            this.hasError.set(true);
          },
        });
      },
      error: () => {
        this.isStreaming.set(false);
        this.hasError.set(true);
      },
    });
  }

  private resendLastMessage(): void {
    this.oauthSubscription?.unsubscribe();
    this.oauthSubscription = null;
    this.oauthPopupInProgress.set(false);
    this.pendingOAuthConsent.set(null);
    this.isStreaming.set(true);
    this.hasError.set(false);

    this.authService.getTokenSilently$().subscribe({
      next: (tokenResponse) => {
        const token = typeof tokenResponse === 'string' ? tokenResponse : (tokenResponse as any).access_token as string;
        this.startStream({
          threadId: this.conversationId ?? undefined,
          messages: [{ role: 'user', content: this.lastUserMessage }],
        }, token);
      },
      error: () => {
        this.isStreaming.set(false);
        this.hasError.set(true);
      },
    });
  }

  submitOAuthConsent(approved: boolean): void {
    const consent = this.pendingOAuthConsent();
    if (!consent) return;

    this.oauthSubscription?.unsubscribe();
    this.oauthSubscription = null;
    this.oauthPopupInProgress.set(false);
    this.pendingOAuthConsent.set(null);
    this.isStreaming.set(true);
    this.hasError.set(false);

    this.authService.getTokenSilently$().subscribe({
      next: (tokenResponse) => {
        const token = typeof tokenResponse === 'string' ? tokenResponse : (tokenResponse as any).access_token as string;
        this.startStream({
          threadId: this.conversationId ?? undefined,
          messages: [],
          oauthConsentRequestId: consent.id,
          oauthConsentApproved: approved,
        }, token);
      },
      error: () => {
        this.isStreaming.set(false);
        this.hasError.set(true);
      },
    });
  }

  private startStream(body: AgentRunBody, token: string): void {
    this.streamAbortController?.abort();
    const controller = new AbortController();
    this.streamAbortController = controller;

    const forwardedProps: Record<string, unknown> = {
      ...(body.mcpApprovalRequestId != null ? { mcpApprovalRequestId: body.mcpApprovalRequestId } : {}),
      ...(body.mcpApprovalApproved != null ? { mcpApprovalApproved: body.mcpApprovalApproved } : {}),
      ...(body.oauthConsentRequestId != null ? { oauthConsentRequestId: body.oauthConsentRequestId } : {}),
      ...(body.oauthConsentApproved != null ? { oauthConsentApproved: body.oauthConsentApproved } : {}),
    };

    const pageContext = this.buildPageContext();

    const requestBody: RunAgentInput = {
      threadId: body.threadId,
      runId: crypto.randomUUID(),
      messages: body.messages ?? [],
      ...(pageContext ? { context: [pageContext] } : {}),
      ...(Object.keys(forwardedProps).length ? { forwardedProps } : {}),
    };

    fetch(this.agentUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(requestBody),
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) {
        this.isStreaming.set(false);
        this.hasError.set(true);
        return;
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop()!;

          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            const data = line.slice(5).trim();
            if (!data || data === '[DONE]') continue;
            try { this.handleEvent(JSON.parse(data)); } catch {}
          }
        }
        this.messages.update(msgs => msgs.map(m =>
          m.isStreaming ? { ...m, isStreaming: false } : m
        ));
        if (this.isStreaming()) this.isStreaming.set(false);
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          this.isStreaming.set(false);
          this.hasError.set(true);
        }
      }
    }).catch((err) => {
      if (err.name !== 'AbortError') {
        this.isStreaming.set(false);
        this.hasError.set(true);
      }
    });
  }

  /**
   * Builds the AG-UI "current_page" context item from the active route, so the agent can resolve
   * "this customer / this valuation" against what the user is looking at. The backend
   * (McpTokenMiddleware) reads the item by its `description`, and WorkflowAgentAdapter appends the
   * `value` JSON to the agent's instructions for that turn. Returns null on pages with no useful
   * context (e.g. login/home) so we don't send noise.
   */
  private buildPageContext(): AguiContextItem | null {
    const path = (this.router.url || '').split('?')[0].split('#')[0];
    const seg = path.split('/').filter(Boolean);
    if (!seg.length) return null;

    // Reverse of parseAppLink: map the plural route segment to the singular resource the agent knows.
    const singular: Record<string, string> = {
      buildings: 'building', valuations: 'valuation', mortgages: 'mortgage',
      applications: 'application', financings: 'financing', tasks: 'task', customers: 'customer',
    };

    const data: Record<string, string> = { url: path };

    if (seg[0] === 'customers' && seg.length >= 2) {
      // /customers/:customerId[/:resource[/:resourceId/detail]]
      // Only ids are sent; the backend resolves them to names/details on demand via the by-id MCP
      // tools (get_customer / get_building / get_valuation), so no name lookup is needed here.
      data['customerId'] = seg[1];
      if (seg.length === 2 || seg[2] === 'detail') {
        data['resource'] = 'customer';
      } else {
        data['resource'] = singular[seg[2]] ?? seg[2];
        if (seg.length >= 5 && seg[seg.length - 1] === 'detail') {
          data['resourceId'] = seg[3];
        }
      }
    } else if (singular[seg[0]]) {
      // Top-level resource route, e.g. /buildings/:id/detail, /tasks/:id/detail
      data['resource'] = singular[seg[0]];
      if (seg.length >= 3 && seg[seg.length - 1] === 'detail') {
        data['resourceId'] = seg[1];
      }
    } else if (seg[0] === 'settings' && seg[1] === 'rates') {
      // The rate settings: the rate open in the rate details tab is selected by query parameters (lender + rate number),
      // keyed like the agent's app://rate/{lenderNumber}_{rateNumber} link.
      data['resource'] = 'rate';
      const query = new URLSearchParams((this.router.url || '').split('?')[1] ?? '');
      const lender = query.get('lender');
      const rate = query.get('rate');
      if (lender && rate) {
        data['resourceId'] = `${lender}_${rate}`;
      }
    } else {
      // Not an entity page (home, settings, login, …) — no useful context to send.
      return null;
    }

    return {
      description: 'current_page',
      value: JSON.stringify(data),
    };
  }

  private handleEvent(event: any): void {
    const type: string = event.type ?? '';

    switch (type) {
      case 'TOOL_CALL_RESULT': {
        const content = event.content;
        if (typeof content === 'string') {
          try {
            const parsed = JSON.parse(content);
            const toolCallId: string = event.toolCallId ?? '';
            if (parsed.type === 'oauth_consent_request') {
              this.messages.update(msgs => msgs.map(m => m.isStreaming ? { ...m, isStreaming: false } : m));
              this.isStreaming.set(false);
              this.pendingOAuthConsent.set({
                id: toolCallId,
                provider: parsed.serverLabel,
                scopes: parsed.scopes,
                authorizationUrl: parsed.consentLink,
                previousResponseId: toolCallId,
              });
              this.oauthSubscription?.unsubscribe();
              this.oauthSubscription = this.mcpAuthService
                .openConsentPopup(parsed.consentLink, this.conversationId ?? '')
                .subscribe({
                  next: () => this.resendLastMessage(),
                  error: () => this.oauthPopupInProgress.set(false),
                });
              this.oauthPopupInProgress.set(true);
            } else if (parsed.type === 'tool_approval_request') {
              this.messages.update(msgs => msgs.map(m => m.isStreaming ? { ...m, isStreaming: false } : m));
              this.isStreaming.set(false);
              this.pendingApproval.set({
                id: toolCallId,
                toolName: parsed.toolName ?? parsed.tool ?? parsed.toolCallName ?? '',
                serverLabel: parsed.serverLabel ?? parsed.server ?? parsed.serverName ?? '',
                arguments: typeof parsed.arguments === 'string' ? parsed.arguments : JSON.stringify(parsed.arguments ?? parsed.input ?? {}),
                previousResponseId: toolCallId,
              });
            } else if (parsed.type === 'question_request') {
              this.messages.update(msgs => msgs.map(m => m.isStreaming ? { ...m, isStreaming: false } : m));
              this.isStreaming.set(false);
              this.pendingQuestion.set({
                question: parsed.question ?? '',
                options: Array.isArray(parsed.options) ? parsed.options : [],
                allowOther: parsed.allowOther ?? false,
                otherLabel: parsed.otherLabel ?? undefined,
              });
            } else if (parsed.type === 'data_changed') {
              // The agent wrote CRM data — notify subscribers to refresh. This is a side-band signal;
              // it must NOT touch the streaming state (the assistant's response is still arriving).
              this.dataChangeService.notify({
                resource: parsed.resource,
                id: parsed.id ?? null,
                customerId: parsed.customerId ?? null,
              });
            }
          } catch {}
        }
        break;
      }

      case 'TEXT_MESSAGE_START':
        if (event.role === 'assistant' && !this.pendingOAuthConsent() && !this.pendingApproval()) {
          this.messages.update(msgs => [...msgs, {
            id: event.messageId,
            role: 'assistant',
            content: '',
            isStreaming: true,
          }]);
        }
        break;

      case 'TEXT_MESSAGE_CONTENT': {
        if (this.pendingOAuthConsent() || this.pendingApproval()) break;
        const delta: string = event.delta ?? '';
        if (!delta) break;
        const messageId: string = event.messageId;
        this.messages.update(msgs => {
          if (messageId && msgs.some(m => m.id === messageId)) {
            return msgs.map(m => m.id === messageId ? { ...m, content: m.content + delta } : m);
          }
          const lastStreamingIdx = msgs.reduce((acc, m, i) =>
            m.role === 'assistant' && m.isStreaming ? i : acc, -1);
          if (lastStreamingIdx >= 0) {
            return msgs.map((m, i) => i === lastStreamingIdx ? { ...m, content: m.content + delta } : m);
          }
          return [...msgs, {
            id: messageId ?? crypto.randomUUID(),
            role: 'assistant' as const,
            content: delta,
            isStreaming: true,
          }];
        });
        break;
      }

      case 'TEXT_MESSAGE_END':
        // Only mark this message as finished. The run may still emit more messages
        // (or a tool/question) before RUN_FINISHED, so leave the global streaming flag
        // to the run-lifecycle events — otherwise the thinking indicator and auto-scroll
        // stop after the first bubble even though updates keep arriving.
        if (!this.pendingOAuthConsent() && !this.pendingApproval()) {
          this.messages.update(msgs => msgs.map(m =>
            m.id === event.messageId ? { ...m, isStreaming: false } : m
          ));
        }
        break;

      case 'RUN_FINISHED':
        if (event.threadId) {
          this.conversationId = event.threadId;
        }
        this.currentResponseId = null;
        this.messages.update(msgs => msgs.map(m =>
          m.isStreaming ? { ...m, isStreaming: false } : m
        ));
        this.isStreaming.set(false);
        break;

      case 'RUN_ERROR':
        this.currentResponseId = null;
        this.isStreaming.set(false);
        this.hasError.set(true);
        break;

      case 'app_link': {
        const value = event as { url: string; label: string };
        const target = parseAppLink(value.url);
        if (target) {
          const lastAssistant = [...this.messages()].reverse().find(m => m.role === 'assistant');
          if (lastAssistant) {
            this.messages.update(msgs => msgs.map(m =>
              m.id === lastAssistant.id
                ? { ...m, appLinks: [...(m.appLinks ?? []), { label: value.label, url: value.url, routerCommands: target.commands, queryParams: target.queryParams }] }
                : m
            ));
          }
        }
        break;
      }

      case 'mcp_approval_request':
        this.messages.update(msgs => msgs.map(m => m.isStreaming ? { ...m, isStreaming: false } : m));
        this.currentResponseId = null;
        this.pendingApproval.set({
          id: event.id,
          toolName: event.toolName,
          serverLabel: event.serverLabel,
          arguments: event.arguments,
          previousResponseId: event.previousResponseId,
        });
        break;

      case 'oauth_consent_request': {
        this.messages.update(msgs => msgs.map(m => m.isStreaming ? { ...m, isStreaming: false } : m));
        this.currentResponseId = null;
        this.pendingOAuthConsent.set({
          id: event.previousResponseId ?? '',
          provider: event.serverLabel,
          scopes: event.scopes,
          authorizationUrl: event.consentLink,
          previousResponseId: event.previousResponseId,
        });
        this.oauthSubscription?.unsubscribe();
        this.oauthSubscription = this.mcpAuthService
          .openConsentPopup(event.consentLink, this.conversationId ?? '')
          .subscribe({
            next: () => this.resendLastMessage(),
            error: () => this.oauthPopupInProgress.set(false),
          });
        this.oauthPopupInProgress.set(true);
        break;
      }
    }
  }

  stopStreaming(): void {
    if (this.currentResponseId) {
      this.agentApi.invoke(cancelResponse, { responseId: this.currentResponseId })
        .catch(err => console.error('Cancel error:', err));
    }
    this.currentResponseId = null;
    this.streamAbortController?.abort();
    this.streamAbortController = null;
    this.oauthSubscription?.unsubscribe();
    this.oauthSubscription = null;
    this.oauthPopupInProgress.set(false);
    this.pendingApproval.set(null);
    this.pendingOAuthConsent.set(null);
    this.pendingQuestion.set(null);
    this.isStreaming.set(false);
    this.messages.update(msgs => {
      const last = msgs[msgs.length - 1];
      if (last?.role === 'assistant' && !last.content) return msgs.slice(0, -1);
      return msgs.map(m => m.isStreaming ? { ...m, isStreaming: false } : m);
    });
  }

  uploadFile(file: File): void {
    this.isUploading.set(true);
    this.authService.getTokenSilently$().subscribe({
      next: (tokenResponse) => {
        const token = typeof tokenResponse === 'string' ? tokenResponse : (tokenResponse as any).access_token as string;
        const form = new FormData();
        form.append('file', file, file.name);
        this.http.post<{ fileId?: string }>(
          `${environment.agentApiBaseUrl}/files`,
          form,
          { headers: { 'Authorization': `Bearer ${token}` } },
        ).subscribe({
          next: (result) => {
            if (result.fileId) {
              this.pendingAttachments.update(list => [
                ...list,
                { fileId: result.fileId!, fileName: file.name },
              ]);
            }
            this.isUploading.set(false);
          },
          error: (err) => {
            console.error('File upload error:', err);
            this.isUploading.set(false);
          },
        });
      },
      error: () => {
        this.isUploading.set(false);
      },
    });
  }

  resolveAppLink(url: string): AppLinkTarget | null {
    return parseAppLink(url.startsWith('unsafe:') ? url.slice(7) : url);
  }

  removeAttachment(fileId: string): void {
    this.pendingAttachments.update(list => list.filter(a => a.fileId !== fileId));
  }

  /** Loads the signed-in user's most recent conversations for the history panel. */
  loadConversations(): void {
    this.isLoadingConversations.set(true);
    this.http.get<ChatConversationSummary[]>(
      `${environment.agentApiBaseUrl}/chat-history/conversations`,
      { params: { limit: 20 } },
    ).subscribe({
      next: (conversations) => {
        this.conversations.set(conversations);
        this.isLoadingConversations.set(false);
      },
      error: (err) => {
        console.error('Load conversations error:', err);
        this.isLoadingConversations.set(false);
      },
    });
  }

  /**
   * Reopens a stored conversation: shows its transcript and makes it the active thread. The backend
   * hydrates the agent from its stored history by threadId, so the next message simply continues it.
   */
  loadConversation(conversationId: string): void {
    if (this.isStreaming()) this.stopStreaming();
    this.clearConversation();
    this.showHistory.set(false);
    this.isLoadingConversation.set(true);

    this.getTranscript(conversationId).subscribe({
      next: (messages) => {
        this.messages.set(messages);
        this.conversationId = conversationId;
        this.isLoadingConversation.set(false);
        // The stored transcript holds only text, so a question the assistant is still waiting on (e.g. the
        // approval of a document processed after an upload) is restored from the pending-runs list.
        this.restorePendingQuestion(conversationId);
      },
      error: (err) => {
        console.error('Load conversation error:', err);
        this.isLoadingConversation.set(false);
        this.hasError.set(true);
      },
    });
  }

  /** Reloads the conversation on screen in place (no spinner, the composer untouched), e.g. while following an upload. */
  private refreshConversation(conversationId: string): void {
    this.getTranscript(conversationId).subscribe({
      next: (messages) => {
        if (this.conversationId !== conversationId || this.isStreaming()) return;
        this.messages.set(messages);
        this.restorePendingQuestion(conversationId);
      },
      error: (err) => console.error('Refresh conversation error:', err),
    });
  }

  private getTranscript(conversationId: string): Observable<ChatMessage[]> {
    return this.http.get<ChatConversationMessage[]>(
      `${environment.agentApiBaseUrl}/chat-history/conversations/${encodeURIComponent(conversationId)}/messages`,
    ).pipe(map(apiMessages => apiMessages.map(m => ({
      id: crypto.randomUUID(),
      role: m.role,
      content: m.content ?? '',
      isStreaming: false,
      attachmentNames: m.attachmentNames?.length ? m.attachmentNames : undefined,
    }))));
  }

  /** Loads the document-processing runs that need the signed-in user's attention (badge + list in the header). */
  loadPendingRuns(): void {
    this.http.get<PendingWorkflowRun[]>(`${environment.agentApiBaseUrl}/workflow-runs/pending`).subscribe({
      next: (runs) => {
        // Following an upload on screen: every push (a step stored, the run reaching a question or its end) refreshes
        // the conversation, the last time with its outcome or open question.
        const followed = this.activeRunWorking() ? this.activeConversationId() : null;
        this.pendingRuns.set(runs ?? []);
        const active = this.activeConversationId();
        if (active && (active === followed || this.activeRunWorking())) this.refreshConversation(active);
      },
      error: (err) => console.error('Load pending workflow runs error:', err),
    });
  }

  /** Opens the conversation of a pending run, with its open question ready to answer. */
  openPendingRun(run: PendingWorkflowRun): void {
    this.loadConversation(run.conversationId);
  }

  private restorePendingQuestion(conversationId: string): void {
    const question = this.pendingRuns().find(r => r.conversationId === conversationId)?.question;
    // A free-text ask has no options; its prompt is already the last message of the transcript.
    if (question?.type !== 'question_request' || !Array.isArray(question.options) || question.options.length === 0) return;

    // The transcript already ends with the question as a bubble; the card replaces it (answering records it again).
    this.messages.update(msgs => {
      const last = msgs[msgs.length - 1];
      return last?.role === 'assistant' && last.content.trim() === (question.question ?? '').trim() ? msgs.slice(0, -1) : msgs;
    });
    this.pendingQuestion.set({
      question: question.question ?? '',
      options: question.options,
      allowOther: question.allowOther ?? false,
      otherLabel: question.otherLabel ?? undefined,
    });
  }

  deleteConversation(conversationId: string): void {
    this.http.delete(
      `${environment.agentApiBaseUrl}/chat-history/conversations/${encodeURIComponent(conversationId)}`,
    ).subscribe({
      next: () => {
        this.conversations.update(list => list.filter(c => c.id !== conversationId));
        if (this.conversationId === conversationId) this.clearConversation();
      },
      error: (err) => console.error('Delete conversation error:', err),
    });
  }

  clearConversation(): void {
    this.oauthSubscription?.unsubscribe();
    this.oauthSubscription = null;
    this.oauthPopupInProgress.set(false);
    this.messages.set([]);
    this.conversationId = null;
    this.pendingAttachments.set([]);
    this.pendingApproval.set(null);
    this.pendingOAuthConsent.set(null);
    this.pendingQuestion.set(null);
    this.hasError.set(false);
  }
}
